import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActions = () => {
  const quickActions = [
    {
      id: 1,
      title: 'Add New Product',
      description: 'Create and publish a new product to your catalog',
      icon: 'Plus',
      color: 'primary',
      action: 'add-product'
    },
    {
      id: 2,
      title: 'Process Orders',
      description: 'Review and fulfill pending customer orders',
      icon: 'Package',
      color: 'success',
      action: 'process-orders',
      badge: '12 pending'
    },
    {
      id: 3,
      title: 'Inventory Check',
      description: 'Review stock levels and reorder alerts',
      icon: 'AlertTriangle',
      color: 'warning',
      action: 'inventory-check',
      badge: '5 alerts'
    },
    {
      id: 4,
      title: 'Customer Support',
      description: 'Respond to customer inquiries and tickets',
      icon: 'MessageCircle',
      color: 'primary',
      action: 'customer-support',
      badge: '3 new'
    },
    {
      id: 5,
      title: 'Sales Report',
      description: 'Generate detailed sales and revenue reports',
      icon: 'BarChart3',
      color: 'success',
      action: 'sales-report'
    },
    {
      id: 6,
      title: 'Bulk Import',
      description: 'Import products from CSV or Excel files',
      icon: 'Upload',
      color: 'primary',
      action: 'bulk-import'
    },
    {
      id: 7,
      title: 'Price Updates',
      description: 'Manage pricing and promotional campaigns',
      icon: 'DollarSign',
      color: 'warning',
      action: 'price-updates'
    },
    {
      id: 8,
      title: 'System Settings',
      description: 'Configure store settings and preferences',
      icon: 'Settings',
      color: 'secondary',
      action: 'system-settings'
    }
  ];

  const recentActivities = [
    {
      id: 1,
      action: 'Product Added',
      description: 'iPhone 15 Pro Max added to catalog',
      timestamp: '2 minutes ago',
      icon: 'Plus',
      color: 'success'
    },
    {
      id: 2,
      action: 'Order Processed',
      description: 'Order #ORD-2024-001 marked as shipped',
      timestamp: '15 minutes ago',
      icon: 'Truck',
      color: 'primary'
    },
    {
      id: 3,
      action: 'Stock Alert',
      description: 'AirPods Pro 2nd Gen is out of stock',
      timestamp: '1 hour ago',
      icon: 'AlertTriangle',
      color: 'warning'
    },
    {
      id: 4,
      action: 'Customer Message',
      description: 'New support ticket from Sarah Johnson',
      timestamp: '2 hours ago',
      icon: 'MessageCircle',
      color: 'primary'
    },
    {
      id: 5,
      action: 'Price Updated',
      description: 'MacBook Pro 16" price reduced by 5%',
      timestamp: '3 hours ago',
      icon: 'DollarSign',
      color: 'success'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: 'from-primary/20 to-primary/10 border-primary/30 text-primary hover:from-primary/30 hover:to-primary/20',
      success: 'from-success/20 to-success/10 border-success/30 text-success hover:from-success/30 hover:to-success/20',
      warning: 'from-warning/20 to-warning/10 border-warning/30 text-warning hover:from-warning/30 hover:to-warning/20',
      secondary: 'from-secondary/20 to-secondary/10 border-secondary/30 text-secondary hover:from-secondary/30 hover:to-secondary/20'
    };
    return colors?.[color] || colors?.primary;
  };

  const getActivityColor = (color) => {
    const colors = {
      primary: 'text-primary',
      success: 'text-success',
      warning: 'text-warning',
      error: 'text-error'
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Quick Actions Grid */}
      <div className="xl:col-span-2">
        <div className="holographic-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-foreground">Quick Actions</h3>
            <Button variant="outline" size="sm" iconName="Grid3X3">
              Customize
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions?.map((action) => (
              <div
                key={action?.id}
                className={`relative p-4 rounded-lg border cursor-pointer transition-all duration-200 bg-gradient-to-br ${getColorClasses(action?.color)} group`}
              >
                {action?.badge && (
                  <div className="absolute -top-2 -right-2 px-2 py-1 bg-error text-error-foreground text-xs font-medium rounded-full border border-error/30">
                    {action?.badge}
                  </div>
                )}
                
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 rounded-lg bg-background/50 group-hover:bg-background/70 transition-colors">
                    <Icon name={action?.icon} size={24} />
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-foreground text-sm mb-1">
                      {action?.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {action?.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Recent Activities */}
      <div className="xl:col-span-1">
        <div className="holographic-card rounded-xl overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-foreground">Recent Activity</h3>
              <div className="flex items-center space-x-1">
                <div className="inventory-pulse high"></div>
                <span className="text-xs text-muted-foreground">Live</span>
              </div>
            </div>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {recentActivities?.map((activity, index) => (
              <div
                key={activity?.id}
                className={`p-4 border-b border-border last:border-b-0 hover:bg-muted/10 transition-colors ${
                  index === 0 ? 'bg-primary/5' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg bg-muted/20 ${getActivityColor(activity?.color)}`}>
                    <Icon name={activity?.icon} size={16} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm mb-1">
                      {activity?.action}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-2 leading-relaxed">
                      {activity?.description}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {activity?.timestamp}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-4 border-t border-border bg-muted/10">
            <Button variant="outline" size="sm" fullWidth iconName="Clock">
              View All Activity
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;